(ns dashboard.subs.event-organizer
  (:require
   [clojure.string :refer [trim]]
   [re-frame.core :as re-frame]
   [dashboard.utils :refer
    [index-by index-of parse-values json-> isOff format-time distinct-by]]
   [dashboard.db.event-organizer :refer [parse-booking]]
   ["date-fns" :refer
    [getMonth startOfMonth endOfMonth eachDayOfInterval format isSameDay isBefore isAfter]]))

(re-frame/reg-sub
 ::events
 (fn [db _]
   (->> (or (:event-organizer/events db) [])
        (map #(assoc %
                     :floorplanConfig (json-> (:floorplanConfig %))
                     :start (js/Date. (:start %))
                     :end (js/Date. (:end %)))))))

(re-frame/reg-sub
 ::events-by-id
 :<- [::events]
 (fn [events]
   (index-by events :id)))

(re-frame/reg-sub
 ::selected-event-id
 (fn [db]
   (get-in db [:event-organizer/selected :event-id])))

(re-frame/reg-sub
 ::selected-event
 :<- [::selected-event-id]
 :<- [::events-by-id]
 (fn [[event-id events-by-id]]
   (get events-by-id event-id)))

(re-frame/reg-sub
 ::floorplan-url
 :<- [::selected-event]
 (fn [event]
   (:floorplanUrl event)))

(re-frame/reg-sub
 ::floorplan-config
 :<- [::selected-event]
 (fn [event]
   (:floorplanConfig event)))

(re-frame/reg-sub
 ::default-selected-date
 :<- [::selected-event]
 :<- [:dashboard.subs/time]
 (fn [[selected-event time]]
   (let [start (js/Date. (:start selected-event))
         end (js/Date. (:end selected-event))]
     (if (or (isBefore time start)
             (isAfter time end))
       (js/Date. (:start selected-event))
       (js/Date.)))))

(re-frame/reg-sub
 ::rooms
 (fn [db _]
   (->> (or (:event-organizer/rooms db) [])
        (map #(assoc % :geometry (json-> (:geometry %)))))))

(re-frame/reg-sub
 ::rooms-by-id
 :<- [::rooms]
 (fn [rooms]
   (index-by rooms :id)))

(re-frame/reg-sub
 ::boutiques
 (fn [db _]
   (:event-organizer/boutiques db)))

(re-frame/reg-sub
 ::boutiques-by-boutique-id
 :<- [::boutiques]
 (fn [boutiques]
   (index-by boutiques :boutiqueId)))

(re-frame/reg-sub
 ::guests
 (fn [db _]
   (:event-organizer/guests db)))

(re-frame/reg-sub
 ::guests-by-id
 :<- [::guests]
 (fn [guests]
   (index-by guests :id)))

(re-frame/reg-sub
 ::bookings
 (fn [db _]
   (->> (:event-organizer/bookings db)
        (map #(assoc %
                     :date (format (js/Date. (:start %)) "yyyy-MM-dd")
                     :start (js/Date. (:start %))
                     :end (js/Date. (:end %))
                     :startTime (format-time (js/Date. (:start %)))
                     :endTime (format-time (js/Date. (:end %)))
                     :checkIn (when (some? (:checkIn %))
                                (js/Date. (:checkIn %)))
                     :enter (when (some? (:enter %))
                              (js/Date. (:enter %)))
                     :exit (when (some? (:exit %))
                             (js/Date. (:exit %)))
                     :clean (when (some? (:clean %))
                              (js/Date. (:clean %)))
                     :cancel (when (some? (:cancel %))
                               (js/Date. (:cancel %)))
                     :checkInTime (when (some? (:checkIn %))
                                    (format-time (js/Date. (:checkIn %))))
                     :checkOut (when (some? (:checkOut %))
                                 (js/Date. (:checkOut %)))
                     :checkOutTime (when (some? (:checkOut %))
                                     (format-time (js/Date. (:checkOut %)))))))))

(re-frame/reg-sub
 ::bookings-count
 :<- [::bookings]
 (fn [bookings]
   (count bookings)))

(re-frame/reg-sub
 ::bookings-file
 (fn [db _]
   (:event-organizer/bookings-file db)))

(re-frame/reg-sub
 ::bookings-file-content
 (fn [db _]
   (:event-organizer/bookings-file-content db)))

(re-frame/reg-sub
 ::file-bookings
 :<- [::bookings-file-content]
 :<- [:dashboard.subs/organization]
 :<- [::selected-event-id]
 :<- [::bookings-by-external-id]
 (fn [[imported-bookings organization event-id bookings-by-external-id]]
   (let [header (map trim (first imported-bookings))]
     (->> imported-bookings
          (rest)
          (map #(apply assoc {} (interleave header %)))
          (map parse-booking)
          (remove #(nil? (:productName %)))
          (map #(assoc %
                       :organizationId (:id organization)
                       :eventId event-id
                       :id (when (some? (:externalId %))
                             (:id (get bookings-by-external-id (:externalId %))))
                       ;; Would be better to do a merge
                       :checkIn (when (some? (:externalId %))
                                  (:checkIn (get bookings-by-external-id (:externalId %))))
                       :enter (when (some? (:externalId %))
                                (:enter (get bookings-by-external-id (:externalId %))))
                       :exit (when (some? (:externalId %))
                               (:exit (get bookings-by-external-id (:externalId %))))
                       :checkOut (when (some? (:externalId %))
                                   (:checkOut (get bookings-by-external-id (:externalId %))))
                       :clean (when (some? (:externalId %))
                                (:clean (get bookings-by-external-id (:externalId %))))
                       :roomId (when (some? (:externalId %))
                                 (:roomId (get bookings-by-external-id (:externalId %))))))
          (sort-by :productName)))))

(re-frame/reg-sub
 ::file-bookings-compared
 :<- [::file-bookings]
 :<- [::bookings]
 (fn [[file-bookings bookings]]
   (let [bookings-by-external-id (index-by bookings :externalId)]
     (->> file-bookings
          (map (fn [imported-booking]
                 (assoc imported-booking
                        :status nil)))))))

(re-frame/reg-sub
 ::bookings-by-id
 :<- [::bookings]
 (fn [bookings]
   (index-by bookings :id)))

(re-frame/reg-sub
 ::bookings-by-external-id
 :<- [::bookings]
 (fn [bookings]
   (index-by bookings :externalId)))

(re-frame/reg-sub
 ::bookings-by-client-id
 :<- [::bookings]
 (fn [bookings]
   (index-by bookings :clientId)))

(re-frame/reg-sub
 ::bookings-by-date
 :<- [::bookings]
 (fn [bookings]
   (group-by :date bookings)))

(re-frame/reg-sub
 ::runners
 (fn [db _]
   (:event-organizer/runners db)))

(re-frame/reg-sub
 ::runners-by-id
 :<- [::runners]
 (fn [runners]
   (index-by runners :id)))
